import { useState, type FormEvent } from "react";
import { Trans } from "@lingui/react/macro";
import type { LicenseTransferInput } from "../api.js";

export function TransferLicenseDialog({ transferVersion, onConfirm, onCancel }: {
  transferVersion: number;
  onConfirm: (input: LicenseTransferInput) => Promise<void>;
  onCancel: () => void;
}) {
  const [fingerprint, setFingerprint] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [attempt, setAttempt] = useState<LicenseTransferInput | null>(null);
  const [error, setError] = useState<string | null>(null);
  const valid = /^(?:[a-f0-9]{4}:){7}[a-f0-9]{4}$/.test(fingerprint.trim());
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!valid || !confirmed || submitting) return;
    const input = attempt ?? { transferId: crypto.randomUUID(), expectedTransferVersion: transferVersion,
      replacementPublicKeyFingerprint: fingerprint.trim() };
    setAttempt(input);
    setSubmitting(true);
    setError(null);
    try { await onConfirm(input); }
    catch (err) { setError(err instanceof Error ? err.message : String(err)); }
    finally { setSubmitting(false); }
  }
  return <div className="modal-backdrop">
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="transfer-title">
      <h2 id="transfer-title"><Trans>Transfer licence</Trans></h2>
      <p><Trans>Open Machine identity on the replacement desktop and copy its fingerprint here.</Trans></p>
      <p><Trans>The previous machine will lose access and the licence key will rotate. History, plan, permissions and consumption stay with this licence.</Trans></p>
      <p><Trans>Sage credentials and local files are not copied. Configure and seal Sage access on the replacement, including the company selected by each retained conversation.</Trans></p>
      <form onSubmit={submit}>
        <label><Trans>Replacement public key fingerprint</Trans>
          <input value={fingerprint} required disabled={submitting || attempt !== null}
            onChange={event => { setFingerprint(event.target.value); setConfirmed(false); }} />
        </label>
        <label><input type="checkbox" checked={confirmed} disabled={submitting || attempt !== null}
          onChange={event => setConfirmed(event.target.checked)} />
          <Trans>I authorize this replacement and revocation of the previous machine.</Trans>
        </label>
        {error && <div className="banner err" role="alert">{error}</div>}
        <div className="actions">
          <button type="button" disabled={submitting} onClick={onCancel}><Trans>Cancel</Trans></button>
          <button type="submit" disabled={!valid || !confirmed || submitting}>
            {submitting ? <Trans>Saving…</Trans> : <Trans>Confirm transfer</Trans>}
          </button>
        </div>
      </form>
    </div>
  </div>;
}
